/**
 * africanDRONE Worldwide Drone Regulations Map
 * Main entry point - initializes globe, loads data, wires UI.
 */
import 'mapbox-gl/dist/mapbox-gl.css';
import './styles/main.css';

import { initGlobe, flyToCountry, applyFog, addTerrain, initStyleSwitcher, getCurrentStyleId } from './map/globe';
import { addCountryLayer } from './map/layers';
import { setupInteractions } from './map/interactions';
import { loadCountries } from './data/countries';
import { COUNTRY_CENTROIDS } from './data/centroids';
import { initSidebar, openSidebar } from './panels/sidebar';
import { initSearch } from './panels/search';
import { initHeader } from './ui/header';
import { initLegend, wireColorOverlayToggle, isColorOverlayActive } from './ui/legend';
import { hideLoading, showDataError } from './ui/loading';
import type { CountryData } from './types';
import { isAdminMode, hasAdminParam, isAdminUnlocked, initAdminState } from './admin/state';
import { initAdminToolbar } from './admin/toolbar';
import { getCountryFromURL, setCountryURL, getPanelFromURL, getTabFromURL } from './url';
import { initLayerPanel, registerLayer, registerAction, isLayerEnabled } from './ui/layers';
import { initResourcesPanel, openResourcesPanel } from './panels/resources-panel';
import { initNewsPanel, openNewsPanel, openOpportunitiesPanel } from './panels/news-panel';
import { addAirspaceLayer, removeAirspaceLayer } from './map/airspace';
import { addDroneAwarenessLayers, removeDroneAwarenessLayers } from './map/drone-awareness';
import { addAirportLayer, removeAirportLayer } from './map/airports';
import { enableMeasure, disableMeasure } from './map/measure';
import { addDroneProjectsLayer, removeDroneProjectsLayer } from './map/drone-projects';
import { createUnitToggle } from './units';

// Mapbox token
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'MAPBOX_TOKEN';

async function main() {
  // Initialize static UI. Header nav buttons call the same actions as the
  // corner layer-panel buttons registered further down (news, opportunities, resources).
  initHeader({
    onNews:          () => openNewsPanel('news'),
    onOpportunities: () => openOpportunitiesPanel('opportunities'),
    onResources:     () => openResourcesPanel('safety'),
  });
  initLegend();

  // Initialize the globe
  const map = initGlobe('map', MAPBOX_TOKEN);

  // Expose map for debugging
  (window as any).__map = map;

  // Initialize sidebar & resources panel
  initSidebar(() => {});
  initResourcesPanel();
  initNewsPanel();

  let firstLoad = true;
  let countries: CountryData[] = [];

  // Re-runs on every style change to restore custom layers
  const addLayers = () => {
    addCountryLayer(map, countries);
    addTerrain(map);
    applyFog(map);

    // Per-style tweaks: border brightness + hover border contrast
    const isDark = getCurrentStyleId() === 'dark';
    if (isDark) {
      // Brighter borders + labels on the dark basemap
      map.setPaintProperty('country-borders', 'line-color', 'rgba(255, 255, 255, 0.30)');
      if (map.getLayer('country-label')) {
        map.setPaintProperty('country-label', 'text-color',       'rgba(255, 255, 255, 0.90)');
        map.setPaintProperty('country-label', 'text-halo-color',  'rgba(0, 0, 0, 0.55)');
      }
    }
    // Hover border: white on dark, dark navy on light/satellite so it always stands out
    map.setPaintProperty(
      'country-borders-hover',
      'line-color',
      isDark ? '#ffffff' : 'rgba(20, 30, 100, 0.85)'
    );

    // Restore color-overlay visibility — persists across style switches
    if (!isColorOverlayActive()) {
      map.setLayoutProperty('country-fills',   'visibility', 'none');
      map.setLayoutProperty('country-borders', 'visibility', 'none');
    }
    // Re-add optional overlay layers if they were toggled on before the style change
    if (isLayerEnabled('airports'))        addAirportLayer(map);
    if (isLayerEnabled('airspace'))        addAirspaceLayer(map);
    if (isLayerEnabled('drone-awareness')) addDroneAwarenessLayers(map);
    if (isLayerEnabled('drone-projects'))  addDroneProjectsLayer(map);
  };

  // style.load fires on init AND on every map.setStyle() call
  map.on('style.load', async () => {
    try {
      if (firstLoad) {
        // Load country data once
        countries = await loadCountries();

        // If the data is missing or empty, show a retryable error and stop.
        // Without this the loading screen hides on a timer and leaves a blank globe.
        if (!countries || countries.length === 0) {
          hideLoading();
          showDataError();
          return;
        }

        // Handler for selecting a country (search or click)
        const handleCountrySelect = (country: CountryData, updateURL = true) => {
          const centroid = COUNTRY_CENTROIDS[country.iso2];
          if (centroid) flyToCountry(map, centroid[0], centroid[1]);
          openSidebar(country);
          if (updateURL) setCountryURL(country.iso2);
        };

        // Deep-link: open country from URL on first load
        const urlIso2 = getCountryFromURL();
        const urlTab = getTabFromURL();
        if (urlIso2) {
          const linked = countries.find(c => c.iso2.toUpperCase() === urlIso2.toUpperCase());
          if (linked) {
            handleCountrySelect(linked, false);
            // Support ?country=SL&tab=regulations (0=overview,1=regulations,2=contacts,3=resources)
            if (urlTab) {
              const tabMap: Record<string, number> = { overview: 0, regulations: 1, contacts: 2, resources: 3, tenders: 4 };
              const tabIdx = tabMap[urlTab.toLowerCase()];
              if (tabIdx !== undefined) {
                const tabBtn = document.querySelector(`.sidebar-tab[data-tab="${tabIdx}"]`) as HTMLElement | null;
                tabBtn?.click();
              }
            }
          }
        }

        // Deep-link: open Resources panel tab from URL (?panel=news, ?panel=events, etc.)
        const urlPanel = getPanelFromURL();
        if (urlPanel && !urlIso2) {
          const validPanels = ['safety', 'equipment', 'manufacturers', 'software', 'education', 'news', 'events'];
          if (validPanels.includes(urlPanel.toLowerCase())) {
            openResourcesPanel(urlPanel.toLowerCase() as any);
          }
        }

        // Wire interactions and search (once only — they use event listeners)
        setupInteractions(map, countries, handleCountrySelect);

        // Airport selection callback — fly to airport coordinates (zoom 12 for airports)
        const handleAirportSelect = (lng: number, lat: number, _name: string) => {
          flyToCountry(map, lng, lat, 12);
        };
        initSearch(countries, handleCountrySelect, handleAirportSelect);

        // Admin mode setup (once only)
        function activateAdmin() {
          initAdminState(countries, map);
          initAdminToolbar();
          document.body.classList.add('admin-mode');
        }
        if (hasAdminParam()) {
          if (isAdminUnlocked()) {
            // Already authenticated this session — activate immediately
            activateAdmin();
          } else {
            // Show password gate before granting access
            const { showPasswordGate } = await import('./admin/password-gate');
            showPasswordGate(activateAdmin);
          }
        }

        // Style switcher UI (once only)
        initStyleSwitcher(map);

        // Wire the legend color-overlay toggle (once only)
        wireColorOverlayToggle(map);

        // ── Topic group (bolder, top): News, Opportunities, Resources, Drones For Good
        registerAction({
          id: 'news',
          label: 'News',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8V6Z"/></svg>',
          onClick: () => openNewsPanel('news'),
        });

        registerAction({
          id: 'opportunities',
          label: 'Opportunities',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>',
          onClick: () => openOpportunitiesPanel('opportunities'),
        });

        registerAction({
          id: 'resources',
          label: 'Resources',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>',
          onClick: () => openResourcesPanel('safety'),
        });

        // Drones For Good — toggleable layer rendered in the bolder topic group
        registerLayer({
          id: 'drone-projects',
          label: 'Drones For Good',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>',
          primary: true,
          onEnable:  (m) => addDroneProjectsLayer(m),
          onDisable: (m) => removeDroneProjectsLayer(m),
        });

        // ── Tools group (separate, below): Airspace, Measure
        registerLayer({
          id: 'airspace',
          label: 'Airspace',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>',
          onEnable:  (m) => { addAirportLayer(m); addAirspaceLayer(m); addDroneAwarenessLayers(m); },
          onDisable: (m) => { removeAirportLayer(m); removeAirspaceLayer(m); removeDroneAwarenessLayers(m); },
        });

        registerLayer({
          id: 'measure',
          label: 'Measure',
          icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M2 2l20 20M6 2v4M2 6h4M18 22v-4M22 18h-4"/></svg>',
          onEnable:  (m) => enableMeasure(m),
          onDisable: (m) => disableMeasure(m),
        });

        // Layer toggle panel (once only)
        initLayerPanel(map);

        // Unit toggle (metric / imperial)
        createUnitToggle();

        hideLoading();
        firstLoad = false;
      }

      // Add/restore data layers on every style load
      addLayers();

    } catch (err) {
      console.error('Failed to initialize map data:', err);
      hideLoading();
      // If country data never loaded, the globe is unusable, so surface a retryable error.
      if (!countries || countries.length === 0) {
        showDataError();
      }
    }
  });

  // Fallback: hide loading after timeout
  setTimeout(hideLoading, 8000);
}

main();
